import { Button, Card } from '../../../design/components'
import { useSceneSession, useSceneSessionStore } from '../sessionState'
import { orderedCast, type SceneCharactersSection } from '../types'
import styles from './play.module.css'

/**
 * Design spec §5.3's turn order: avatars in initiative order, the acting combatant
 * ember-ringed, and a "next up" hint.
 *
 * **Device-local, like the exchange counter, and for the same reason** — the API has no
 * initiative or turn-order concept. What *is* server-provided is the cast: this card
 * reads the scene's characters and imposes a local ordering on them, so somebody leaving
 * the scene disappears from the order without any bookkeeping here.
 *
 * "Ember-ringed" is not the only signal the acting entry carries: it also has visually
 * hidden text, because the spec's own §8 forbids meaning carried by colour alone.
 */
export function TurnOrderCard({
  sceneId,
  characters,
}: {
  sceneId: number
  characters: SceneCharactersSection | undefined
}) {
  const session = useSceneSession(sceneId)
  const setTurnOrder = useSceneSessionStore((state) => state.setTurnOrder)
  const advanceTurn = useSceneSessionStore((state) => state.advanceTurn)
  const setActing = useSceneSessionStore((state) => state.setActing)

  const cast = characters?.all ?? []
  if (cast.length === 0) return null

  const arranged = orderedCast(cast, session.turnOrder)
  const acting = arranged[session.actingIndex % arranged.length]
  const next = arranged[(session.actingIndex + 1) % arranged.length]

  return (
    <Card variant="skills" label="Turn order" hint="On this device">
      <div className={styles.turnOrder}>
        {arranged.map((entry, index) => {
          const isActing = entry.characterId === acting?.characterId

          return (
            <button
              key={entry.id}
              type="button"
              className={`${styles.turnEntry} ${isActing ? styles.turnEntryActing : ''}`}
              // Tapping somebody hands them the turn — faster at a table than stepping
              // through, and it is how a GM actually corrects the order mid-exchange.
              // The order is pinned first, because until it is, `arranged` is just the
              // cast's own order and the index would not survive the cast changing.
              onClick={() => {
                setTurnOrder(
                  sceneId,
                  arranged.map((candidate) => candidate.characterId),
                )
                setActing(sceneId, index)
              }}
            >
              {entry.portraitUrl !== null && entry.portraitUrl !== '' ? (
                <img className={styles.turnAvatar} src={entry.portraitUrl} alt="" loading="lazy" />
              ) : (
                <span className={styles.turnAvatarFallback} aria-hidden="true">
                  {entry.name.slice(0, 1).toUpperCase()}
                </span>
              )}
              <span>{entry.name}</span>
              {isActing && <span className="sr-only">acting now</span>}
            </button>
          )
        })}
      </div>

      {next !== undefined && arranged.length > 1 && (
        <p className={styles.nextUp}>Next up: {next.name}</p>
      )}

      <div className={styles.actions}>
        <Button
          onClick={() => {
            // Arranging on first use means the order is the cast's order until somebody
            // says otherwise, rather than an empty card asking to be set up.
            if (session.turnOrder.length === 0) {
              setTurnOrder(
                sceneId,
                arranged.map((entry) => entry.characterId),
              )
            }
            advanceTurn(sceneId)
          }}
        >
          Next turn
        </Button>
        <Button variant="secondary" onClick={() => setTurnOrder(sceneId, [])}>
          Clear order
        </Button>
      </div>
    </Card>
  )
}
